// Bloco "Transcrição de reunião" dentro das páginas: botão para iniciar,
// controles durante a gravação e, no fim, resumo, anotações e transcrição.

import * as recording from "./recording.js";
import * as store from "./store.js";
import { el, clock, fmtDateTime, debounce, toast, menu, confirmDialog, dialog } from "./dom.js";
import { api } from "./api.js";
import { tasksPanel, autoCreate } from "./meeting-tasks.js";

const TABS = [["summary", "Resumo"], ["notes", "Anotações"], ["transcript", "Transcrição"]];

// opts: {pageId, pageTitle(), editable(), getMeetingId(), setMeetingId(id)}
export function mountMeetingBlock(dom, opts) {
  let tab = null;
  let timer = null;
  let poll = null;
  let destroyed = false;
  const offs = [];

  const id = () => opts.getMeetingId();
  const live = () => recording.current && recording.current.meetingId === id();

  const render = async () => {
    clearInterval(timer);
    clearTimeout(poll);
    if (destroyed) return;
    const mid = id();
    if (!mid) return renderIdle();
    if (live()) return renderLive();
    let data = recording.cachedMeeting(mid);
    if (!data) {
      dom.replaceChildren(head("Transcrição de reunião", "Carregando…"));
      try {
        data = await recording.fetchMeeting(mid);
      } catch (err) {
        if (destroyed || id() !== mid) return;
        return dom.replaceChildren(head("Transcrição de reunião", err.status === 404 ? "Esta transcrição foi apagada ou pertence a outra pessoa." : "Sem conexão para carregar a transcrição."),
          err.status === 404 && opts.editable() ? el("div", { class: "mb-actions" }, el("button", { onclick: () => opts.setMeetingId(null) }, "Começar uma nova")) : null);
      }
      if (destroyed || id() !== mid) return;
    }
    renderDone(data);
  };

  function head(title, sub, ...actions) {
    return el("div", { class: "mb-head" },
      el("span", { class: "mb-icon" }, "🎙"),
      el("div", { class: "mb-title" }, el("strong", {}, title), sub ? el("span", { class: "mb-sub" }, sub) : null),
      el("div", { class: "mb-head-actions" }, actions));
  }

  function renderIdle() {
    const busy = recording.current;
    dom.className = "meeting-block idle";
    if (!store.canTranscribe()) {
      return dom.replaceChildren(head("Transcrição de reunião", "A transcrição de chamadas é só para administradores do Ordo."));
    }
    dom.replaceChildren(
      head("Transcrição de reunião", window.ordo?.web
        ? "Grava o seu microfone e, se você compartilhar a aba ou a tela com áudio, o som da chamada. Transcreve e resume aqui."
        : "Grava o seu microfone e o áudio do computador, transcreve e resume aqui."),
      opts.editable()
        ? el("div", { class: "mb-actions" },
          el("button", { class: "primary rec-btn", disabled: !!busy, onclick: startHere }, "● Iniciar transcrição"),
          el("button", { onclick: sendBot, title: "O Ordo entra na chamada do Google Meet como participante e anota quem fala" }, "🤖 Enviar o bot ao Meet"),
          busy ? el("span", { class: "muted small" }, "Já há uma transcrição em andamento.") : null)
        : null);
  }

  // Ordo Bot: entra na reunião do Google Meet (roda no servidor, não precisa deste computador ligado).
  function sendBot() {
    const input = el("input", { type: "url", placeholder: "https://meet.google.com/abc-defg-hij", autofocus: true });
    const error = el("p", { class: "error small" });
    const go = el("button", { class: "primary", type: "submit" }, "Enviar o bot");
    dialog((close) => el("form", { onsubmit: async (e) => {
      e.preventDefault();
      const url = input.value.trim();
      if (!/^https:\/\/meet\.google\.com\/[a-z]{3,4}-[a-z]{3,4}-[a-z]{3,4}/i.test(url)) {
        error.textContent = "Cole o link da reunião do Google Meet (meet.google.com/abc-defg-hij).";
        return;
      }
      go.disabled = true;
      try {
        const mid = crypto.randomUUID();
        await api("POST", "/meetings", { id: mid, page_id: opts.pageId, app: "Google Meet", language: "pt", title: opts.pageTitle() || undefined, startedAt: Date.now() });
        const res = await api("POST", `/meetings/${mid}/bot`, { url });
        opts.setMeetingId(mid);
        close();
        toast(res.bot?.status === "queued" ? "O bot está a caminho da reunião." : "Bot enviado.");
        render();
      } catch (err) {
        error.textContent = err.message;
        go.disabled = false;
      }
    } },
    el("h3", {}, "🤖 Enviar o bot ao Google Meet"),
    el("p", { class: "muted small" }, "O Ordo entra na chamada como participante \"Ordo (transcrição)\", grava, anota quem está falando e, quando a reunião acaba, faz o resumo e as tarefas aqui. Se a reunião tiver sala de espera, alguém precisa aceitar o bot."),
    el("label", { class: "field" }, "Link da reunião", input), error,
    el("div", { class: "dlg-actions" }, el("button", { type: "button", onclick: close }, "Cancelar"), go)));
  }

  async function startHere() {
    const mid = crypto.randomUUID();
    opts.setMeetingId(mid);
    const ok = await recording.start({ meetingId: mid, pageId: opts.pageId, title: opts.pageTitle() || undefined });
    if (!ok && id() === mid) opts.setMeetingId(null);
    render();
  }

  let wasLive = false;
  function renderLive() {
    wasLive = true;
    const r = recording.current;
    dom.className = "meeting-block live" + (r.pausedAt ? " paused" : "");
    const time = el("span", { class: "mb-clock" }, clock(recording.elapsed()));
    const lvlMic = el("span", { class: "lvl-bar" });
    const lvlSys = el("span", { class: "lvl-bar" });
    const pending = el("span", { class: "muted small" });
    const levels = el("div", { class: "mb-levels" },
      el("span", { class: "lvl" }, "Você", el("span", { class: "lvl-track" }, lvlMic)),
      el("span", { class: "lvl" }, "Outros", el("span", { class: "lvl-track" }, lvlSys)), pending);
    offs.push(recording.on("level", ({ track, level }) => {
      (track === "mic" ? lvlMic : lvlSys).style.width = Math.min(100, level * 400) + "%";
    }));
    offs.push(recording.on("pending", (n) => { pending.textContent = n ? `${n} trecho(s) transcrevendo…` : ""; }));
    dom.replaceChildren(
      head(r.pausedAt ? "Pausado" : r.starting ? "Começando…" : "Transcrevendo", null,
        time,
        r.pausedAt
          ? el("button", { onclick: () => recording.resume() }, "▶ Retomar")
          : el("button", { disabled: r.starting, onclick: () => recording.pause() }, "❚❚ Pausar"),
        el("button", { class: "stop", disabled: r.starting, onclick: () => recording.stop() }, "■ Encerrar")),
      levels,
      tabs(["notes", "transcript"], { meeting: recording.cachedMeeting(id())?.meeting || {}, segments: recording.cachedMeeting(id())?.segments || [] }, true));
    timer = setInterval(() => { time.textContent = clock(recording.elapsed()); }, 500);
  }

  function renderDone(data) {
    const { meeting } = data;
    // Acabou de encerrar: mostra o resumo.
    if (wasLive) { wasLive = false; tab = "summary"; }
    const mid = meeting.id;
    const finishing = recording.isFinishing(mid) || meeting.status === "summarizing";
    dom.className = "meeting-block done";
    const dur = meeting.ended_at ? ` · ${clock(meeting.ended_at - meeting.started_at)}` : "";
    const sub = `${fmtDateTime(meeting.started_at)}${dur}${meeting.app ? " · " + meeting.app : ""}`;
    const more = opts.editable() ? el("button", { class: "icon-btn", title: "Mais opções", onclick: (e) => menu(e.currentTarget, [
      store.canTranscribe() && { label: "Refazer resumo", icon: "↻", run: () => recording.summarize(mid).catch((err) => toast(err.message)) },
      { label: "Copiar tudo", icon: "⧉", run: () => copyAll(data) },
      "-",
      { label: "Apagar transcrição", icon: "🗑", danger: true, run: async () => {
        if (!(await confirmDialog("Apagar esta transcrição e o resumo? Não dá para desfazer.", { ok: "Apagar", danger: true }))) return;
        await recording.remove(mid).catch((err) => toast(err.message));
        opts.setMeetingId(null);
        render();
      } },
    ], { align: "right" }) }, "⋯") : null;
    dom.replaceChildren(head(meeting.title || "Reunião", sub, more));
    const bot = data.bot;
    const botActive = bot && ["queued", "joining", "waiting", "recording", "stopping"].includes(bot.status);
    if (botActive) {
      const label = { queued: "a caminho da reunião…", joining: "entrando na reunião…", waiting: "esperando alguém aceitar na reunião…",
        recording: "na reunião, gravando e anotando quem fala", stopping: "saindo da reunião…" }[bot.status];
      dom.append(el("div", { class: "mb-note mb-bot" },
        el("p", {}, el("span", { class: "rec-dot" }), ` Bot ${label}`, bot.error ? el("span", { class: "muted small" }, ` (${bot.error})`) : null),
        opts.editable() && bot.status !== "stopping" ? el("button", { onclick: async () => {
          await api("POST", `/meetings/${mid}/bot/stop`, {}).catch((err) => toast(err.message));
          await recording.fetchMeeting(mid).catch(() => {});
          render();
        } }, bot.status === "queued" ? "Cancelar" : "■ Tirar o bot e resumir") : null));
      poll = setTimeout(async () => { await recording.fetchMeeting(mid).catch(() => {}); render(); }, 5000);
    } else if (bot?.status === "error" && meeting.status !== "done") {
      dom.append(el("div", { class: "mb-note" }, el("p", { class: "error" }, bot.error || "O bot não conseguiu gravar a reunião.")));
    }
    if (meeting.status === "recording" && !finishing && !botActive && !bot) {
      dom.append(el("div", { class: "mb-note" },
        el("p", {}, "A gravação foi interrompida antes de terminar (o app foi fechado?)."),
        opts.editable() && store.canTranscribe() ? el("button", { class: "primary", onclick: () => recording.summarize(mid).catch((err) => toast(err.message)) }, "Gerar resumo com o que foi gravado") : null));
    }
    dom.append(tabs(["summary", "notes", "transcript"], data, false));
    // Resumo pronto e "Criar sempre automaticamente" ligado: as tarefas vão direto para o quadro.
    if (!finishing && opts.editable() && meeting.tasks?.items?.length && !meeting.tasks.created) {
      autoCreate(meeting).then((done) => { if (done && !destroyed) rerender(); });
    }
    if (finishing && !botActive) poll = setTimeout(async () => { await recording.fetchMeeting(mid).catch(() => {}); render(); }, 4000);
  }

  function tabs(names, data, isLive) {
    if (!tab || !names.includes(tab)) tab = names[0];
    const body = el("div", { class: "mb-body" });
    const bar = el("div", { class: "mb-tabs" }, names.map((n) => el("button", {
      class: "mb-tab" + (n === tab ? " active" : ""),
      onclick: () => { tab = n; body.replaceWith(tabs(names, data, isLive)); },
    }, TABS.find((t) => t[0] === n)[1])));
    const panel = el("div", { class: "mb-panel" });
    if (tab === "summary") panel.append(summaryPanel(data));
    if (tab === "notes") panel.append(notesPanel(data));
    if (tab === "transcript") panel.append(transcriptPanel(data, isLive));
    body.append(bar, panel);
    return body;
  }

  function summaryPanel({ meeting }) {
    const finishing = recording.isFinishing(meeting.id) || meeting.status === "summarizing";
    const box = el("div", { class: "mb-summary" });
    if (finishing) box.append(el("p", { class: "muted" }, el("span", { class: "spinner" }), " Gerando o resumo…"));
    else if (meeting.status === "error") box.append(el("p", { class: "error" }, `O resumo falhou: ${meeting.error || ""}. Use ⋯ → Refazer resumo.`));
    else if (meeting.summary) box.innerHTML = window.OrdoMarkdown.render(meeting.summary);
    else box.append(el("p", { class: "muted" }, "Sem resumo ainda."));
    const tasks = finishing ? null : tasksPanel(meeting, { editable: opts.editable(), onChange: () => rerender() });
    return el("div", {}, box, tasks, askBox(meeting.id));
  }

  function notesPanel({ meeting }) {
    const ta = el("textarea", { class: "mb-notes", placeholder: "Anote o que importa. Estas anotações entram no resumo.", readonly: !opts.editable() });
    ta.value = meeting.notes || "";
    const status = el("span", { class: "muted small" });
    const save = debounce(async () => {
      try {
        await recording.saveNotes(meeting.id, ta.value);
        status.textContent = "Salvo";
      } catch (err) {
        status.textContent = "Não salvou: " + err.message;
      }
    }, 700);
    ta.addEventListener("input", () => { status.textContent = "Salvando…"; save(); grow(); });
    ta.addEventListener("blur", () => save.flush());
    const grow = () => { ta.style.height = "auto"; ta.style.height = Math.max(90, ta.scrollHeight) + "px"; };
    requestAnimationFrame(grow);
    return el("div", {}, ta, status);
  }

  function transcriptPanel({ segments }, isLive) {
    const list = el("div", { class: "mb-transcript" });
    const add = (s) => list.append(el("div", { class: "seg " + s.track },
      el("span", { class: "seg-time" }, clock(s.start_ms)),
      el("span", { class: "seg-who", title: s.speaker || "" }, s.speaker || (s.track === "mic" ? "Você" : s.track === "bot" ? "Participante" : "Outros")),
      el("span", { class: "seg-text" }, s.text)));
    if (!segments.length) list.append(el("p", { class: "muted empty-seg" }, isLive ? "As falas aparecem aqui a cada ~30 segundos." : "Nenhuma fala transcrita."));
    segments.forEach(add);
    if (isLive) {
      offs.push(recording.on("segment", ({ meetingId, seg }) => {
        if (meetingId !== id()) return;
        list.querySelector(".empty-seg")?.remove();
        add(seg);
      }));
    }
    return list;
  }

  function askBox(meetingId) {
    const input = el("input", { placeholder: "Pergunte algo sobre esta reunião…" });
    const answer = el("div", { class: "mb-answer", hidden: true });
    const form = el("form", { class: "ask", onsubmit: async (e) => {
      e.preventDefault();
      const q = input.value.trim();
      if (!q) return;
      answer.hidden = false;
      answer.replaceChildren(el("p", { class: "muted" }, "Pensando…"));
      try {
        const res = await api("POST", "/ask", { question: q, meetingId }, { timeout: 120000 });
        answer.innerHTML = window.OrdoMarkdown.render(res.answer);
      } catch (err) {
        answer.replaceChildren(el("p", { class: "error" }, err.message));
      }
    } }, input, el("button", { type: "submit" }, "Perguntar"));
    return el("div", { class: "mb-ask" }, form, answer);
  }

  async function copyAll({ meeting, segments }) {
    const parts = [`# ${meeting.title}`, meeting.summary || ""];
    if (meeting.notes) parts.push("## Anotações", meeting.notes);
    parts.push("## Transcrição", segments.map((s) => `[${clock(s.start_ms)}] ${s.speaker || (s.track === "mic" ? "Você" : "Outros")}: ${s.text}`).join("\n"));
    await navigator.clipboard.writeText(parts.join("\n\n"));
    toast("Copiado.");
  }

  const rerender = () => {
    for (const off of offs.splice(0)) off();
    render();
  };
  const offState = recording.on("state", (r) => {
    // Começou/parou/pausou: redesenha (o bloco ocioso também, para habilitar o botão).
    if (!r || r.meetingId === id() || !id()) rerender();
  });
  const offStatus = recording.on("status", (mid) => { if (mid === id()) rerender(); });
  render();

  return {
    refresh: rerender,
    destroy: () => {
      destroyed = true;
      clearInterval(timer);
      clearTimeout(poll);
      offState();
      offStatus();
      for (const off of offs.splice(0)) off();
    },
  };
}
